import { createFileRoute, Link, useParams, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTracking } from "@/hooks/use-tracking";
import { 
  Loader2, 
  BadgeCheck, 
  MapPin, 
  Phone, 
  Globe, 
  Mail, 
  Heart,
  MessageCircle,
  AlertCircle,
  Building2,
  ChevronLeft,
  CheckCircle2, 
  Clock, 
  XCircle, 
  ArrowRight,
  Store
} from "lucide-react";
import { FornecedorPerfilSkeleton } from "@/components/ui/skeletons";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ReportModal } from "@/components/ReportModal";
import { ClaimModal } from "@/components/ClaimModal";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getIcone } from "@/lib/icones";

export const Route = createFileRoute("/_authenticated/fornecedores/$id")({
  component: FornecedorPerfil,
});

function FornecedorPerfil() {
  const { id } = useParams({ from: '/_authenticated/fornecedores/$id' });
  const { user } = useAuth();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [fornecedor, setFornecedor] = useState<any>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const { registrar } = useTracking();

  const { data: userClaim, isLoading: loadingClaim } = useQuery({
    queryKey: ["user-claim", id, user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("reivindicacoes")
        .select("*")
        .eq("fornecedor_id", id)
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!id,
  });

  const { data: isFavorite } = useQuery({
    queryKey: ['is-favorite', id, user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data, error } = await supabase
        .from('favoritos')
        .select('id')
        .eq('fornecedor_id', id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) return false;
      return !!data;
    },
    enabled: !!user && !!id,
  });

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) return;
    
    // Optimistic update
    const previousFavorite = isFavorite;
    queryClient.setQueryData(['is-favorite', id, user.id], !previousFavorite);

    try {
      if (previousFavorite) {
        const { error, count } = await supabase
          .from('favoritos')
          .delete({ count: 'exact' })
          .eq('fornecedor_id', id)
          .eq('user_id', user.id);
        
        if (error) throw error;
        if (count === 0) {
          toast.warning("Não foi possível remover: registro não encontrado.");
          queryClient.setQueryData(['is-favorite', id, user.id], true);
          return;
        }
        toast.success("Removido dos Favoritos");
      } else {
        const { data, error } = await supabase
          .from('favoritos')
          .insert({ fornecedor_id: id, user_id: user.id })
          .select();
        
        if (error) throw error;
        if (!data || data.length === 0) {
          toast.warning("Não foi possível salvar: verifique seu acesso.");
          queryClient.setQueryData(['is-favorite', id, user.id], false);
          return;
        }
        toast.success("Salvo em Favoritos", {
          action: { label: "Ver Favoritos", onClick: () => navigate({ to: '/favoritos' }) }
        });
      }

      queryClient.invalidateQueries({ queryKey: ['is-favorite', id] });
      queryClient.invalidateQueries({ queryKey: ['favoritos'] });
    } catch (error: any) {
      // Revert on error
      queryClient.setQueryData(['is-favorite', id, user.id], previousFavorite);
      toast.error("Erro ao atualizar favoritos");
      console.error(error);
    }
  };

  const { data: hasExistingReport } = useQuery({
    queryKey: ["existing-report", id, user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("denuncias")
        .select("id")
        .eq("fornecedor_id", id)
        .eq("user_id", user.id)
        .eq("status", "aberta")
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !!id,
  });

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('fornecedores')
        .select('*, segmentos(nome, icone)')
        .eq('id', id)
        .single();
      
      if (data) {
        setFornecedor(data);
        // Track view
        const key = `view_perfil_${id}`;
        if (!sessionStorage.getItem(key)) {
          registrar('view_perfil', { fornecedor_id: id });
          sessionStorage.setItem(key, '1');
        }
      }
      setLoading(false);
    }
    load();
  }, [id]);

  if (loading) return (
    <div className="bg-secondary/30">
      <div className="bg-white border-b h-16" />
      <FornecedorPerfilSkeleton />
    </div>
  );

  if (!fornecedor) return <div className="p-20 text-center">Fornecedor não encontrado.</div>;

  const cleanPhone = fornecedor.telefone?.replace(/\D/g, '') || '';
  const waUrl = `https://wa.me/55${cleanPhone}?text=${encodeURIComponent("Olá! Encontrei vocês na Lista de Fornecedores da Gopage e gostaria de saber mais sobre os produtos.")}`;

  const handleContactClick = (tipo: any, url: string) => {
    registrar(tipo, { fornecedor_id: id });
    window.open(url, '_blank');
  };

  return (
      <div className="bg-secondary/30 pb-20">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="container mx-auto px-4 h-16 flex items-center">
            <Link to="/fornecedores" search={{}} className="flex items-center gap-2 text-sm font-semibold hover:text-primary">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-5xl">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div className="bg-white p-8 rounded-3xl border shadow-sm">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="w-24 h-24 bg-secondary rounded-2xl flex items-center justify-center font-bold text-3xl text-brand-dark">
                    {fornecedor.logo_url ? <img src={fornecedor.logo_url} className="w-full h-full object-contain" /> : fornecedor.nome[0]}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h1 className="text-3xl font-bold">{fornecedor.nome}</h1>
                      {fornecedor.verificado && (
                        <Badge className="bg-primary text-brand-dark gap-1">
                          <BadgeCheck className="w-3 h-3" /> Verificado
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-4">{fornecedor.razao_social}</p>
                    <div className="flex flex-wrap gap-y-2 gap-x-6 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        {(() => {
                          const Icon = getIcone(fornecedor.segmentos?.icone);
                          return <Icon className="w-4 h-4 text-primary" />;
                        })()}
                        {fornecedor.segmentos?.nome}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4 text-primary" /> {fornecedor.cidade}/{fornecedor.uf} {fornecedor.polo && `• ${fornecedor.polo}`}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant={isFavorite ? "onLight" : "outlineLight"} 
                    size="icon" 
                    className={`rounded-full ${isFavorite ? 'text-red-500 bg-red-50' : ''}`}
                    onClick={toggleFavorite}
                  >
                    <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Contatos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fornecedor.telefone && (
                  <Button 
                    className="h-16 rounded-2xl bg-[#25D366] text-white font-bold hover:bg-[#25D366]/90 gap-3 text-lg"
                    onClick={() => handleContactClick('click_whatsapp', waUrl)}
                  >
                    <MessageCircle className="w-6 h-6" /> WhatsApp
                  </Button>
                )}
                {fornecedor.site && (
                  <Button 
                    variant="outlineLight"
                    className="h-16 rounded-2xl font-bold gap-3 text-lg"
                    onClick={() => handleContactClick('click_site', fornecedor.site)}
                  >
                    <Globe className="w-6 h-6" /> Visitar Site
                  </Button>
                )}
                {fornecedor.instagram && (
                  <Button 
                    variant="outlineLight"
                    className="h-16 rounded-2xl font-bold gap-3 text-lg"
                    onClick={() => handleContactClick('click_instagram', `https://instagram.com/${fornecedor.instagram.replace('@', '')}`)}
                  >
                    <Globe className="w-6 h-6" /> Instagram
                  </Button>
                )}
                {fornecedor.email && (
                  <Button 
                    variant="outlineLight"
                    className="h-16 rounded-2xl font-bold gap-3 text-lg"
                    onClick={() => handleContactClick('click_email', `mailto:${fornecedor.email}`)}
                  >
                    <Mail className="w-6 h-6" /> Enviar E-mail
                  </Button>
                )}
              </div>

              {/* Informações */}
              <Card className="rounded-3xl p-8 shadow-sm">
                <h2 className="text-xl font-bold mb-6">Sobre o fornecedor</h2>
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <span className="block text-xs font-bold uppercase text-muted-foreground mb-1">Pedido Mínimo</span>
                    <p className="font-semibold text-lg">{fornecedor.pedido_minimo || 'Não informado'}</p>
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase text-muted-foreground mb-1">Atende Varejo?</span>
                    <p className="font-semibold text-lg">{fornecedor.atende_varejo ? 'Sim' : 'Somente Atacado'}</p>
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase text-muted-foreground mb-1">Descrição</span>
                  <p className="text-muted-foreground leading-relaxed">
                    {fornecedor.descricao || 'Nenhuma descrição detalhada disponível.'}
                  </p>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              {fornecedor.verificado ? (
                <Card className="bg-[#C6F764]/10 border-[#C6F764]/20 rounded-3xl p-6">
                  <div className="flex gap-4 items-start mb-4">
                    <div className="p-2 bg-primary rounded-xl">
                      <BadgeCheck className="w-6 h-6 text-brand-dark" />
                    </div>
                    <h3 className="font-bold text-lg">Fornecedor Verificado</h3>
                  </div>
                  <p className="text-sm text-brand-dark/70 leading-relaxed">
                    Este fornecedor é cliente Gopage e teve o CNPJ conferido pela nossa equipe, garantindo maior segurança para sua compra.
                  </p>
                </Card>
              ) : (!fornecedor.owner_id && (
                <Card className="bg-secondary/50 border-border rounded-3xl p-6">
                  {!userClaim ? (
                    <>
                      <h3 className="font-bold mb-2">É desta empresa?</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Reivindique sua ficha e ganhe o selo de Verificado para passar mais confiança aos lojistas.
                      </p>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto font-bold text-brand-dark gap-1"
                        onClick={() => setIsClaimModalOpen(true)}
                      >
                        Reivindicar agora <ArrowRight className="w-4 h-4" />
                      </Button>
                    </>
                  ) : userClaim.status === 'pendente' ? (
                    <>
                      <div className="flex items-center gap-2 text-amber-600 mb-2">
                        <Clock className="w-5 h-5" />
                        <h3 className="font-bold">Solicitação em análise</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Nossa equipe está verificando seus dados. Você receberá um retorno em até 2 dias úteis.
                      </p>
                    </>
                  ) : userClaim.status === 'aprovada' ? (
                    <>
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <CheckCircle2 className="w-5 h-5" />
                        <h3 className="font-bold">Ficha reivindicada por você</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Você agora é o proprietário desta ficha na Lista de Fornecedores.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-red-600 mb-2">
                        <XCircle className="w-5 h-5" />
                        <h3 className="font-bold">Solicitação recusada</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Motivo: {userClaim.motivo_rejeicao || "Não foi possível validar seu vínculo."}
                      </p>
                      <Button 
                        variant="outlineDark" 
                        className="w-full rounded-xl font-bold"
                        onClick={() => setIsClaimModalOpen(true)}
                      >
                        Reenviar solicitação
                      </Button>
                    </>
                  )}
                </Card>
              ))}

              <Button 
                variant="ghost" 
                className="w-full text-muted-foreground gap-2" 
                onClick={() => setIsReportModalOpen(true)}
                disabled={!!hasExistingReport}
              >
                <AlertCircle className="w-4 h-4" /> 
                {hasExistingReport ? "Você já reportou este fornecedor" : "Reportar informação incorreta"}
              </Button>
            </div>
          </div>
        </div>

        <ReportModal 
          open={isReportModalOpen} 
          onOpenChange={setIsReportModalOpen} 
          fornecedorId={id}
          fornecedorNome={fornecedor.nome}
        />

        <ClaimModal
          open={isClaimModalOpen}
          onOpenChange={setIsClaimModalOpen}
          fornecedorId={id}
          fornecedorNome={fornecedor.nome}
          fornecedorCnpj={fornecedor.cnpj || ""}
        />
      </div>
    
  );
}
